import React, { useState, useEffect } from "react";
import { Patient, PrescriptionGroup, Prescription } from "common/models/types";
import PrescriptionGroupList from "./PrescriptionGroupList";
import PrescriptionGroupForm from "./PrescriptionGroupForm";
import { log, LogLevel } from "utils/LogUtil";

interface PrescriptionsTabProps {
  patient: Patient;
  onReloadPatient?: (patientId: number) => void;
  isPrescriptionLoading?: boolean;
  onSavePrescriptionGroup?: (group: PrescriptionGroup) => void;
  onDeletePrescriptionGroup?: (groupId: string, prescriptions: Prescription[]) => void;
}

const PrescriptionsTab: React.FC<PrescriptionsTabProps> = ({
  patient,
  onReloadPatient,
  isPrescriptionLoading,
  onSavePrescriptionGroup: externalSavePrescriptionGroup,
  onDeletePrescriptionGroup: externalDeletePrescriptionGroup,
}) => {
  // 처방전 그룹 관련 상태
  const [editingPrescriptionGroup, setEditingPrescriptionGroup] =
    useState<PrescriptionGroup | null>(null);

  // 처방전 그룹 편집 핸들러
  const handleEditPrescriptionGroup = (
    groupId: string,
    prescriptionDate: Date | string,
    prescriptions: Prescription[]
  ) => {
    log(LogLevel.INFO, "처방전 그룹 편집 시작:", {
      groupId,
      prescriptionDate,
      prescriptionCount: prescriptions.length,
    });
    const prescriptionGroup: PrescriptionGroup = {
      groupId: groupId,
      prescriptionDate,
      prescriptions,
      deletedPrescriptionIds: [],
    };
    setEditingPrescriptionGroup(prescriptionGroup);
  };

  // 처방전 그룹 편집 취소 핸들러
  const handleCancelEditPrescriptionGroup = () => {
    setEditingPrescriptionGroup(null);
  };

  // 처방전 그룹 저장 핸들러
  const handleSavePrescriptionGroup = async () => {
    if (!editingPrescriptionGroup) return;

    try {
      // 디버깅: 저장 전 처방전 그룹 데이터 로깅
      log(
        LogLevel.VERBOSE,
        "저장 전 처방전 그룹 데이터:",
        JSON.stringify(editingPrescriptionGroup, null, 2)
      );

      // 처방전 데이터 유효성 검증
      const validPrescriptions = editingPrescriptionGroup.prescriptions.map((prescription) => {
        // 기존 처방전 복사
        const validPrescription = { ...prescription };

        // 필수 필드 설정 확인
        if (!validPrescription.patientId && patient.id) {
          validPrescription.patientId = patient.id;
        }

        // 그룹 ID 및 처방일 설정
        validPrescription.prescriptionGroupId = editingPrescriptionGroup.groupId;
        validPrescription.prescriptionDate = editingPrescriptionGroup.prescriptionDate;

        return validPrescription;
      });

      // 유효한 처방전으로 업데이트된 그룹
      const validGroup = {
        ...editingPrescriptionGroup,
        prescriptions: validPrescriptions,
      };

      log(LogLevel.VERBOSE, "검증 후 처방전 그룹 데이터:", JSON.stringify(validGroup, null, 2));

      // 외부 저장 함수가 있으면 호출
      if (externalSavePrescriptionGroup) {
        await externalSavePrescriptionGroup(validGroup);
      } else {
        // API 호출 로직 (실제 구현 시 추가 필요)
        log(LogLevel.INFO, "처방전 그룹 저장:", validGroup);

        // 환자 정보 다시 로드
        if (onReloadPatient && patient.id) {
          await onReloadPatient(patient.id as number);
        }
      }

      // 편집 상태 초기화
      setEditingPrescriptionGroup(null);
    } catch (error) {
      log(LogLevel.ERROR, "처방전 그룹 저장 오류:", error);
    }
  };

  // 처방전 그룹 삭제 핸들러
  const handleDeletePrescriptionGroup = async (groupId: string, prescriptions: Prescription[]) => {
    try {
      // 외부 삭제 함수가 있으면 호출
      if (externalDeletePrescriptionGroup) {
        await externalDeletePrescriptionGroup(groupId, prescriptions);
      } else {
        // API 호출 로직 (실제 구현 시 추가 필요)
        log(LogLevel.INFO, "처방전 그룹 삭제:", { groupId, prescriptions });

        // 환자 정보 다시 로드
        if (onReloadPatient && patient.id) {
          await onReloadPatient(patient.id as number);
        }
      }
    } catch (error) {
      log(LogLevel.ERROR, "처방전 그룹 삭제 오류:", error);
    }
  };

  // 처방전 추가 핸들러
  const handleAddPrescription = () => {
    // 디버깅 로그 추가
    log(LogLevel.INFO, "처방전 추가 버튼 클릭됨");

    // 새 처방전 그룹 생성 로직
    const newGroup: PrescriptionGroup = {
      groupId: `temp-${Date.now()}`,
      prescriptionDate: new Date(),
      prescriptions: [
        {
          patientId: patient.id as number,
          medicationName: "",
          prescriptionDate: new Date(),
        },
      ],
      deletedPrescriptionIds: [],
    };
    setEditingPrescriptionGroup(newGroup);
  };

  // 처방전 항목 추가 핸들러
  const handleAddPrescriptionItem = () => {
    if (!editingPrescriptionGroup) return;

    setEditingPrescriptionGroup({
      ...editingPrescriptionGroup,
      prescriptions: [
        ...editingPrescriptionGroup.prescriptions,
        {
          patientId: patient.id as number,
          medicationName: "",
          prescriptionDate: editingPrescriptionGroup.prescriptionDate,
        },
      ],
    });
  };

  // 처방전 항목 업데이트 핸들러
  const handleUpdatePrescriptionItem = (prescription: Prescription, index: number) => {
    if (!editingPrescriptionGroup) return;

    log(LogLevel.VERBOSE, "처방전 항목 업데이트:", {
      약품명: prescription.medicationName,
      보험코드: prescription.insuranceCode,
      아이템코드: prescription.itemCode,
    });

    const updatedPrescriptions = [...editingPrescriptionGroup.prescriptions];
    updatedPrescriptions[index] = prescription;

    setEditingPrescriptionGroup({
      ...editingPrescriptionGroup,
      prescriptions: updatedPrescriptions,
    });
  };

  // 처방전 항목 삭제 핸들러
  const handleDeletePrescriptionItem = (index: number) => {
    if (!editingPrescriptionGroup) return;

    const updatedPrescriptions = [...editingPrescriptionGroup.prescriptions];
    updatedPrescriptions.splice(index, 1);

    setEditingPrescriptionGroup({
      ...editingPrescriptionGroup,
      prescriptions: updatedPrescriptions,
    });
  };

  // 처방전 날짜 업데이트 핸들러
  const handleUpdatePrescriptionDate = (date: Date) => {
    if (!editingPrescriptionGroup) return;

    setEditingPrescriptionGroup({
      ...editingPrescriptionGroup,
      prescriptionDate: date,
    });
  };

  return (
    <>
      {/* 처방전 그룹 편집 UI */}
      {editingPrescriptionGroup ? (
        <PrescriptionGroupForm
          prescriptionGroup={editingPrescriptionGroup}
          onUpdatePrescriptionDate={handleUpdatePrescriptionDate}
          onAddPrescriptionItem={handleAddPrescriptionItem}
          onUpdatePrescriptionItem={handleUpdatePrescriptionItem}
          onDeletePrescriptionItem={handleDeletePrescriptionItem}
          onSave={handleSavePrescriptionGroup}
          onCancel={handleCancelEditPrescriptionGroup}
        />
      ) : (
        /* 처방전 그룹 목록 - 편집 중이 아닐 때만 표시 */
        <PrescriptionGroupList
          patient={patient}
          editingPrescriptionGroup={editingPrescriptionGroup}
          onEditPrescriptionGroup={handleEditPrescriptionGroup}
          onDeletePrescriptionGroup={handleDeletePrescriptionGroup}
          onAddPrescription={handleAddPrescription}
        />
      )}
    </>
  );
};

export default React.memo(PrescriptionsTab);
